/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useCallback, useEffect } from 'react';

import styled, { css } from 'styled-components';

import { isLocale } from '~/shared/utils/helpers';
import useGshop from '~/shared/utils/useGshop';
import { Button, ProductPrice } from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import errorMessages from './errorMessages';
import {
  useProductDetailCTAContext,
  ProductDetailCTAErrorTypes,
} from './ProductDetailCTAContext';

const Wrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.guestCtaWrapper};
  display: flex;
  flex-direction: column;
`;

const AddToBagButton = styled(Button).attrs({
  variant: 'primary',
  size: 'lg',
})`
  margin-top: 16px;

  ${({ theme }) => theme.pdpStyles.guestAddToBag.button}

  ${({ isFRDomain, theme }) =>
    isFRDomain &&
    css`
      background: ${theme.colors.palette.white};
      border: 1px solid ${theme.colors.alertRed};
      span {
        color: ${theme.colors.alertRed};
      }
      :hover,
      :focus,
      :focus-visible,
      :active {
        outline: none;
        background: ${theme.colors.palette.white};
      }
    `}
`;

const labelStyle = css`
  font-size: 16px;
  letter-spacing: ${-0.4 / 18}em;
  color: ${({ theme }) => theme.colors.palette.white};

  ${({ theme }) => theme.pdpStyles.guestAddToBag.label}
`;

const priceStyle = css`
  font-weight: 700;
`;

function GuestAddToBag({ ...rest }) {
  const intl = useIntl();
  const {
    addToCart,
    handleSharedValidation,
    isLoading,
    isOutOfStock,
    setErrorMessage,
    hasError,
  } = useProductDetailCTAContext();

  const disabled = isLoading || isOutOfStock;
  const { tld } = useDomain();
  const isFRDomain = isLocale(['FR'], tld);

  useEffect(() => {
    if (!hasError) {
      setErrorMessage(null);
    }
  }, [hasError, setErrorMessage]);
  const { isGshopActive } = useGshop();
  const handleCTAClick = useCallback(async () => {
    try {
      await handleSharedValidation();
      await addToCart({ enableVIPCart: false });
    } catch (error) {
      if (error.message === ProductDetailCTAErrorTypes.SelectorError) {
        setErrorMessage(intl.formatMessage(errorMessages.sizeSelectorError));
      }
    }
  }, [addToCart, handleSharedValidation, intl, setErrorMessage]);

  return (
    <Wrapper {...rest}>
      <span tabIndex={0}>
        <FormattedMessage
          id="pdp_membership_select.guest_titleV3"
          defaultMessage="Guest add to cart: {1}"
          values={{
            1: (
              <ProductPrice
                showVipPrice={isGshopActive}
                showRetailPrice={!isGshopActive}
                showPromoPrice={false}
                priceStyle={priceStyle}
              />
            ),
          }}
        />
      </span>

      <AddToBagButton
        type="button"
        variant="secondary"
        labelStyle={labelStyle}
        onClick={handleCTAClick}
        data-autotag={
          disabled ? 'pdp-guest-add-to-bag-disabled' : 'pdp-guest-add-to-bag'
        }
        disabled={disabled}
        isFRDomain={isFRDomain}
        data-cnstrc-btn="add-to-cart"
      >
        {isOutOfStock ? (
          <FormattedMessage
            id="global_cta.out_of_stock"
            defaultMessage="Out of Stock"
          />
        ) : (
          <FormattedMessage
            id="global_cta.add_to_bag_guest"
            defaultMessage="Guest Add to Bag"
          />
        )}
      </AddToBagButton>
    </Wrapper>
  );
}

export default GuestAddToBag;
